import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { DeleteSupplierDocumentPayload } from './types';
import { supplierDocumentsQueryKey } from './use-get-supplier-documents-api';

const getEndpoint = (supplierProfileId: number, documentId: number) =>
  `ums/v1/cms/supplier-profiles/${supplierProfileId}/documents/${documentId}`;

type TData = { id: number };
type TError = AxiosError;
type TVariables = DeleteSupplierDocumentPayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useDeleteSupplierDocumentApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ supplierProfileId, documentId }: TVariables) => {
    try {
      const { data } = await http.delete(getEndpoint(supplierProfileId, documentId));
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: ['supplier-documents/delete'],
    onSuccess: (data, variables, context) => {
      toast({ description: 'Document removed successfully', status: 'success' });
      queryClient.invalidateQueries([supplierDocumentsQueryKey, variables.supplierProfileId]);
      if (userOnSuccess) userOnSuccess(data, variables, context);
    },
    ...restOptions,
  });
}
